import { fetchFromDataApi, createDataApiFullUrl } from './DataApiClient';

type MarketCandlesResult = {
	success: boolean;
	status?: number;
	records?: Array<{
		ts: number;
		fillOpen: number;
		fillHigh: number;
		fillLow: number;
		fillClose: number;
		oracleOpen: number;
		oracleHigh: number;
		oracleLow: number;
		oracleClose: number;
		baseVolume: string;
		quoteVolume: string;
	}>;
};

/**
 * Client for fetching global stats and market-level data from the Data API.
 * These endpoints are not user-specific and don't fit the short-term/long-term pattern.
 *
 * For user-specific paginated history data, use DataApiClient instead.
 *
 * All methods delegate the transport (URL building, fetch, JSON parsing,
 * never-throw outcome classification) to `fetchFromDataApi` — each method only
 * declares its route and how to shape a successful body into its envelope.
 */
class StatsApiClient {
	/**
	 * Shared request policy for all stats endpoints, on top of the Data API
	 * transport core. Never throws:
	 * - HTTP errors resolve to `{ success: false }` with no other fields,
	 *   unless the method provides `shapeHttpError` (candles report `status`).
	 * - Network errors and body-shaping failures are logged and resolve to
	 *   `{ success: false }`.
	 */
	private static async getStats<TResult extends { success: boolean }>(
		path: string,
		queryString: string,
		errorLabel: string,
		shapeBody: (body: any) => TResult,
		shapeHttpError?: (status: number) => TResult
	): Promise<TResult | { success: false }> {
		const result = await fetchFromDataApi(path, queryString);

		if (!result.ok) {
			if (result.kind === 'http') {
				return shapeHttpError
					? shapeHttpError(result.status)
					: { success: false };
			}

			console.error(
				`Error fetching ${errorLabel} from data API:`,
				result.error
			);
			return { success: false };
		}

		try {
			return shapeBody(result.body);
		} catch (e) {
			console.error(`Error fetching ${errorLabel} from data API:`, e);
			return { success: false };
		}
	}

	/**
	 * Fetch insurance fund stats from the data API
	 * Endpoint: /stats/insuranceFund
	 */
	public static async fetchInsuranceFundStats(): Promise<{
		success: boolean;
		data?: {
			totalRevenue: string;
			perpLiqsTotal: string;
			spotLiqsTotal: string;
			marketSharePriceData: Array<{
				marketIndex: number;
				apy: string | null;
			}>;
		};
	}> {
		return this.getStats(
			'/stats/insuranceFund',
			'',
			'insurance fund stats',
			(body) => ({
				success: true,
				data: body.data ?? body,
			})
		);
	}

	/**
	 * Fetch funding rates from the data API
	 * Endpoint: /market/:symbol/fundingRates
	 */
	public static async fetchFundingRates(
		symbol: string,
		limit = 750
	): Promise<{
		success: boolean;
		records?: Array<{
			ts: number;
			marketIndex: number;
			fundingRate: string;
			fundingRateLong: string;
			fundingRateShort: string;
			oraclePriceTwap: string;
			markPriceTwap: string;
		}>;
		meta?: { nextPage: string | null };
	}> {
		const queryParams = new URLSearchParams();
		queryParams.set('limit', limit.toString());

		return this.getStats(
			`/market/${symbol}/fundingRates`,
			`?${queryParams.toString()}`,
			'funding rates',
			(body) => ({
				success: body.success ?? true,
				records: body.records ?? [],
				meta: body.meta ?? { nextPage: null },
			})
		);
	}

	/**
	 * Fetch market summary (24h data for all markets)
	 * Endpoint: GET /stats/markets
	 */
	public static async fetchMarketSummary(): Promise<{
		success: boolean;
		markets?: Array<{
			symbol: string;
			marketIndex: number;
			marketType: string;
			oraclePrice: string;
			markPrice: string;
			baseVolume: string;
			quoteVolume: string;
			deposits?: string;
			borrows?: string;
			openInterest?: { long: string; short: string };
			fundingRate?: { long: string; short: string };
			fundingRate24h?: string;
			fundingRateUpdateTs?: number;
			price: string;
			priceChange24h: string;
			priceChange24hPercent: string;
			priceHigh?: { oracle: string; fill: string };
			priceLow?: { oracle: string; fill: string };
		}>;
	}> {
		return this.getStats('/stats/markets', '', 'market summary', (body) => ({
			success: body.success ?? true,
			markets: body.markets ?? [],
		}));
	}

	/**
	 * Fetch average funding rates for all markets
	 * Endpoint: GET /stats/fundingRates
	 */
	public static async fetchAvgFundingRates(): Promise<{
		success: boolean;
		markets?: Array<{
			marketIndex: number;
			symbol: string;
			fundingRates: {
				'24h': string;
				'7d': string;
				'30d': string;
				'1y': string;
			};
		}>;
	}> {
		return this.getStats(
			'/stats/fundingRates',
			'',
			'avg funding rates',
			(body) => ({
				success: body.success ?? true,
				markets: body.markets ?? [],
			})
		);
	}

	/**
	 * Fetch bankruptcy records and stats
	 * Endpoint: GET /stats/bankruptcies
	 */
	public static async fetchBankruptcies(pageToken?: string): Promise<{
		success: boolean;
		stats?: {
			totalAmount: string;
			ifPayment: string;
			socialLoss: string;
			totalCount: string;
		};
		records?: Array<Record<string, unknown>>;
		meta?: { nextPage: string | null; totalRecords?: number };
	}> {
		const queryParams = new URLSearchParams();
		if (pageToken) {
			queryParams.set('page', pageToken);
		}

		const queryString = queryParams.toString();

		return this.getStats(
			'/stats/bankruptcies',
			queryString ? `?${queryString}` : '',
			'bankruptcies',
			(body) => ({
				success: body.success ?? true,
				stats: body.stats,
				records: body.records ?? [],
				meta: {
					nextPage: body.meta?.nextPage ?? null,
					totalRecords: body.meta?.totalRecords,
				},
			})
		);
	}

	/**
	 * Fetch liquidation records and stats
	 * Endpoint: GET /stats/liquidations
	 */
	public static async fetchLiquidations(pageToken?: string): Promise<{
		success: boolean;
		stats?: {
			'24h': { count: string; amount: string };
			'30d': { count: string; amount: string };
		};
		records?: Array<Record<string, unknown>>;
		meta?: { nextPage: string | null };
	}> {
		const queryParams = new URLSearchParams();
		if (pageToken) {
			queryParams.set('page', pageToken);
		}

		const queryString = queryParams.toString();

		return this.getStats(
			'/stats/liquidations',
			queryString ? `?${queryString}` : '',
			'liquidations',
			(body) => ({
				success: body.success ?? true,
				stats: body.stats,
				records: body.records ?? [],
				meta: body.meta ?? { nextPage: null },
			})
		);
	}

	/**
	 * Fetch rate history for a spot market
	 * Endpoint: GET /stats/:symbol/rateHistory/:type
	 */
	public static async fetchRateHistory(
		symbol: string,
		type: 'deposit' | 'borrow'
	): Promise<{
		success: boolean;
		rates?: Array<[number, string]>;
	}> {
		return this.getStats(
			`/stats/${symbol}/rateHistory/${type}`,
			'',
			'rate history',
			(body) => ({
				success: body.success ?? true,
				rates: body.rates ?? [],
			})
		);
	}

	/**
	 * Fetch candles for a market
	 * Endpoint: GET /market/:symbol/candles/:resolution
	 */
	public static async fetchMarketCandles(params: {
		symbol: string;
		resolution: '1' | '5' | '15' | '60' | '240' | 'D' | 'W' | 'M';
		limit?: number;
		startTs?: number;
		endTs?: number;
	}): Promise<MarketCandlesResult> {
		// Clamp timestamps to current time - API rejects future timestamps
		const now = Math.floor(Date.now() / 1000);
		const clampedStartTs = params.startTs
			? Math.min(params.startTs, now)
			: undefined;
		const clampedEndTs = params.endTs ? Math.min(params.endTs, now) : undefined;

		const queryParams = new URLSearchParams();
		if (params.limit) {
			queryParams.set('limit', params.limit.toString());
		}
		if (clampedStartTs) {
			queryParams.set('startTs', clampedStartTs.toString());
		}
		if (clampedEndTs) {
			queryParams.set('endTs', clampedEndTs.toString());
		}

		const queryString = queryParams.toString();

		return this.getStats<MarketCandlesResult>(
			`/market/${params.symbol}/candles/${params.resolution}`,
			queryString ? `?${queryString}` : '',
			'market candles',
			(body) => ({
				success: body.success ?? true,
				records: body.records ?? [],
			}),
			(status) => ({ success: false, status })
		);
	}

	/**
	 * Fetch insurance fund records for a spot market
	 * Endpoint: GET /market/:symbol/insuranceFund
	 */
	public static async fetchMarketInsuranceFundRecords(
		symbol: string,
		pageToken?: string
	): Promise<{
		success: boolean;
		records?: Array<Record<string, unknown>>;
		meta?: { nextPage: string | null };
	}> {
		const queryParams = new URLSearchParams();
		if (pageToken) {
			queryParams.set('page', pageToken);
		}

		const queryString = queryParams.toString();

		return this.getStats(
			`/market/${symbol}/insuranceFund`,
			queryString ? `?${queryString}` : '',
			'insurance fund records',
			(body) => ({
				success: body.success ?? true,
				records: body.records ?? [],
				meta: body.meta ?? { nextPage: null },
			})
		);
	}

	/**
	 * Fetch the monthly maker/taker volume leaderboard for a given year/month.
	 * Endpoint: GET /stats/{maker|taker}-leaderboard/:year/:month
	 *
	 * Replaces the old static S3 JSON at
	 * .../analytics/public/(taker_)leaderboard/YYYY/MM/(taker_)leaderboard.json.
	 * The API wraps the payload as { success, data: { leaderboard, lastUpdated, month } };
	 * this returns the inner `data`. `lastUpdated`/`month` are null when no snapshot
	 * exists yet for the requested month.
	 */
	public static async fetchMonthlyLeaderboard<T>(
		role: 'maker' | 'taker',
		year: number,
		month: number
	): Promise<{
		success: boolean;
		data?: {
			leaderboard: T[];
			lastUpdated: number | null;
			month: string | null;
		};
	}> {
		try {
			const fullUrl = createDataApiFullUrl(
				`/stats/${role}-leaderboard/${year}/${month}`,
				''
			);
			const response = await fetch(fullUrl);

			if (!response.ok) {
				return { success: false };
			}

			const body = await response.json();

			return {
				success: body.success ?? true,
				data: body.data,
			};
		} catch (e) {
			console.error(`Error fetching ${role} leaderboard from data API:`, e);
			return { success: false };
		}
	}
}

export default StatsApiClient;
